import { api, CHAT_DOCUMENTS_TAG } from '@/api';
import { setAllDocumentsListData, setDocumentsListData, type ChatDocument } from '@/store';
import { handleQueryLifecycle } from '@/api/utils.ts';
import { FileStatus } from '@/types/file';

export interface GetChatDocumentsRequest {
  chatId: string | undefined;
}

export interface Document {
  documentId: string;
  documentName: string;
  uploadedAt?: string;
  sizeMb?: number;
  messageId?: string;
  /**
   * Generated drafts live outside the FletchDocument table, so `documentId` is a
   * `draftVersionId` and only the `/chat/draft/*` endpoints can open or download them.
   */
  isDraft?: boolean;
}

export interface GetChatDocumentResponse {
  documents: Document[];
  allDocuments: Document[];
}

export const getChatDocumentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getChatDocuments: build.query<GetChatDocumentResponse, GetChatDocumentsRequest>({
      query: ({ chatId }) => ({
        method: 'GET',
        url: '/chat/documents',
        params: {
          chat_id: chatId,
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: CHAT_DOCUMENTS_TAG, id: arg.chatId },
        { type: CHAT_DOCUMENTS_TAG, id: 'LIST' },
      ],
      async onQueryStarted({ chatId }, { dispatch, queryFulfilled }) {
        await handleQueryLifecycle(queryFulfilled, (data) => {
          const withUploadedStatus = (documents: Document[]) =>
            documents.map((doc) => ({
              ...doc,
              status: FileStatus.UPLOADED,
            })) as ChatDocument[];

          // `documents` is the sendable set; `allDocuments` also carries drafts, which the
          // send endpoint rejects with a 404, so it only feeds the file listing.
          dispatch(setDocumentsListData({ chatId, documents: withUploadedStatus(data.documents) }));
          dispatch(
            setAllDocumentsListData({ chatId, documents: withUploadedStatus(data.allDocuments ?? data.documents) }),
          );
        });
      },
    }),
  }),
});

export const { useGetChatDocumentsQuery, useLazyGetChatDocumentsQuery } = getChatDocumentsApi;
